// frontend/src/pages/club/ClubTournaments/ClubTournaments.jsx
import React, { useEffect, useState, useCallback } from 'react';
import GradientButton from '../../../components/common/GradientButton';
import Icon from '../../../components/common/Icon';
import Card from '../../../components/common/Card';
import Pagination from '../../../components/common/Pagination';
import StatusBadge from '../../../components/common/StatusBadge';
import { getTournamentsApi, registerClubToTournamentApi } from '../../../api/tournament.api';
import {
  TOURNAMENT_TYPE_LABELS,
  TOURNAMENT_STATUS_LABELS,
  REGISTRATION_STATUS_LABELS,
} from '../../../utils/constants';
import { toJalali } from '../../../utils/jalali';

const REGISTRATION_BADGE_MAP = {
  PENDING: 'pending',
  APPROVED: 'active',
  REJECTED: 'inactive',
};

const TOURNAMENT_BADGE_MAP = {
  OPEN: 'active',
  ONGOING: 'pending',
  COMPLETED: 'inactive',
  CLOSED: 'inactive',
};

export default function ClubTournaments() {
  const [tournaments, setTournaments] = useState([]);
  const [filters, setFilters] = useState({ status: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [registeringId, setRegisteringId] = useState(null);
  const [snackbar, setSnackbar] = useState('');

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const { data } = await getTournamentsApi({
        ...clean,
        page: pagination.page,
        limit: pagination.limit,
      });
      setTournaments(data.data);
      setPagination((prev) => ({ ...prev, total: data.meta.total, totalPages: data.meta.totalPages }));
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت تورنمنت‌ ها');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  const handleRegister = async (tournamentId) => {
    setRegisteringId(tournamentId);
    try {
      await registerClubToTournamentApi(tournamentId, {});
      setSnackbar('درخواست ثبت‌ نام ارسال شد و در انتظار تایید مدیر سیستم است');
      loadTournaments();
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در ثبت‌ نام');
    } finally {
      setRegisteringId(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">تورنمنت‌ ها</h1>
        <p className="mt-1 text-sm text-text-dark/60">
          می‌ توانید در تورنمنت‌ هایی که برای ثبت‌ نام باز هستند عضو شوید. تایید نهایی عضویت بر عهده‌ی مدیر
          سیستم هیئت فوتبال شهرستان پاوه است.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">همه وضعیت‌ ها</option>
          {Object.entries(TOURNAMENT_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>
            ✕
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-text-dark/50">در حال بارگذاری...</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {tournaments.map((t) => (
            <Card key={t.id} className="space-y-3 p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-black text-slate-800">{t.name}</h3>
                  <p className="mt-1 text-xs text-slate-400">
                    {TOURNAMENT_TYPE_LABELS[t.type] || t.type}
                  </p>
                </div>
                <StatusBadge
                  status={TOURNAMENT_BADGE_MAP[t.status] || 'inactive'}
                  label={TOURNAMENT_STATUS_LABELS[t.status] || t.status}
                />
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Icon name="calendar" className="h-3.5 w-3.5" />
                <span>شروع: {toJalali(t.startDate)}</span>
                {t.endDate && <span>· پایان: {toJalali(t.endDate)}</span>}
              </div>

              {t.description && <p className="text-sm text-slate-600">{t.description}</p>}

              <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                {t.myRegistration ? (
                  <StatusBadge
                    status={REGISTRATION_BADGE_MAP[t.myRegistration.status] || 'pending'}
                    label={`وضعیت باشگاه شما: ${
                      REGISTRATION_STATUS_LABELS[t.myRegistration.status] || t.myRegistration.status
                    }`}
                  />
                ) : (
                  <span className="text-xs text-slate-400">باشگاه شما هنوز ثبت‌ نام نکرده است</span>
                )}

                {!t.myRegistration && t.status === 'OPEN' && (
                  <GradientButton
                    onClick={() => handleRegister(t.id)}
                    disabled={registeringId === t.id}
                  >
                    {registeringId === t.id ? 'در حال ارسال...' : 'ثبت‌ نام باشگاه'}
                  </GradientButton>
                )}
              </div>
            </Card>
          ))}

          {tournaments.length === 0 && (
            <div className="col-span-full py-12 text-center text-slate-400">
              تورنمنتی یافت نشد
            </div>
          )}
        </div>
      )}

      <Pagination page={pagination.page} totalPages={pagination.totalPages} onPageChange={handlePageChange} />
    </div>
  );
}
